/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, GitBranch } from 'lucide-react';

/**
 * Lightweight SVG referral graph — no external deps.
 *
 * 3 levels deep, ecosystem-coloured. Click a node to recenter the tree on
 * that lead; click "back to all roots" to return.
 */
const ECO_PALETTE = [
  '#4A5D4E', '#8C6239', '#5C7A8A', '#7A6A8C', '#8C6A6A',
  '#6A8C7A', '#A89460', '#608A8C', '#8C8060', '#7A8A6A',
  '#5C5955', '#A87A60', '#6A7A8C',
];
function ecoColor(code) {
  if (!code) return '#B5B0A8';
  let h = 0; for (const c of code) h = (h * 31 + c.charCodeAt(0)) | 0;
  return ECO_PALETTE[Math.abs(h) % ECO_PALETTE.length];
}

const BAND_TONE = {
  STRONG: { ring: '#10B981', label: 'strong' },
  EMERGING: { ring: '#F59E0B', label: 'emerging' },
  WEAK: { ring: '#A8A29E', label: 'weak' },
};

export default function ReferralGraphPage() {
  const [data, setData] = useState(null);
  const [rootId, setRootId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ecosystems, setEcosystems] = useState([]);

  useEffect(() => {
    setLoading(true);
    const url = rootId
      ? `${API}/terra-growth/intelligence/referral-graph?root_lead_id=${rootId}&depth=3`
      : `${API}/terra-growth/intelligence/referral-graph?depth=3`;
    Promise.all([axios.get(url), axios.get(`${API}/terra-growth/ecosystems`).catch(() => ({ data: { ecosystems: [] } }))])
      .then(([r, e]) => { setData(r.data); setEcosystems(e.data?.ecosystems || []); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [rootId]);

  const layout = useMemo(() => buildTreeLayout(data), [data]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="referral-graph-page" className="max-w-7xl mx-auto px-6 py-8">
        <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
            <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Referral Graph</h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
              Three levels deep. Each node carries ecosystem, perception state, and RSS band.
              Click a node to recenter the tree.
            </p>
          </div>
          {rootId && (
            <button data-testid="referral-back-btn"
                    onClick={() => setRootId(null)}
                    className="text-[11px] rounded-full px-3 py-1 border bg-white text-[#5C5955] border-[#D8D4CE]">
              ← Back to all roots
            </button>
          )}
        </header>

        {loading ? (
          <Skeleton />
        ) : !data || data.nodes.length === 0 ? (
          <Empty />
        ) : (
          <>
            <Legend ecosystems={ecosystems} graph={data} />
            <Canvas layout={layout} onNodeClick={setRootId} />
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function Skeleton() {
  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
      <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Tracing the chain…
    </div>
  );
}
function Empty() {
  return (
    <div data-testid="referral-graph-empty"
         className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
      No referral chains yet. As leads start referring others, the graph will fill in.
    </div>
  );
}

function Legend({ ecosystems, graph }) {
  // Show only ecosystems that appear in the graph.
  const ecoCodes = Array.from(new Set(graph.nodes.map(n => n.ecosystem_code).filter(Boolean)));
  return (
    <div data-testid="referral-graph-legend"
         className="rounded-2xl border border-[#EBE8E3] bg-white p-3 mb-4 flex flex-wrap items-center gap-3">
      <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">Ecosystems in view</span>
      {ecoCodes.map(c => (
        <span key={c} className="inline-flex items-center gap-1 text-[10px] text-[#5C5955]">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: ecoColor(c) }} />
          {ecosystems.find(e => e.code === c)?.name || c}
        </span>
      ))}
      <span className="ml-auto text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">
        {graph.nodes.length} nodes · {graph.edges.length} referrals
      </span>
    </div>
  );
}

// ── lightweight tree layout ─────────────────────────────────────────────
function buildTreeLayout(data) {
  if (!data || data.nodes.length === 0) return null;
  // Group nodes by level.
  const byLevel = {};
  data.nodes.forEach(n => {
    (byLevel[n.level] = byLevel[n.level] || []).push(n);
  });
  const levels = Object.keys(byLevel).map(Number).sort((a, b) => a - b);
  const ROW_H = 130, NODE_W = 200;
  const positioned = [];
  let maxRowWidth = 0;
  levels.forEach(lvl => {
    const items = byLevel[lvl];
    const rowWidth = items.length * NODE_W;
    maxRowWidth = Math.max(maxRowWidth, rowWidth);
    items.forEach((n, i) => {
      positioned.push({ ...n, x: 50 + i * NODE_W + NODE_W / 2, y: 50 + lvl * ROW_H });
    });
  });
  // Map id → pos for edges.
  const posMap = positioned.reduce((m, n) => { m[n.id] = n; return m; }, {});
  return {
    width:  Math.max(800, maxRowWidth + 100),
    height: 50 + (levels.length) * ROW_H + 80,
    nodes:  positioned,
    edges:  data.edges.map(e => ({ ...e, fromPos: posMap[e.from], toPos: posMap[e.to] })),
  };
}

function Canvas({ layout, onNodeClick }) {
  if (!layout) return null;
  return (
    <div data-testid="referral-graph-canvas"
         className="rounded-2xl border border-[#EBE8E3] bg-white overflow-x-auto p-4">
      <svg width={layout.width} height={layout.height} className="block min-w-full">
        {layout.edges.map((e, i) => (
          e.fromPos && e.toPos && (
            <path key={i} data-testid={`graph-edge-${i}`}
                  d={`M ${e.fromPos.x} ${e.fromPos.y + 28}
                       C ${e.fromPos.x} ${e.fromPos.y + 80},
                         ${e.toPos.x} ${e.toPos.y - 50},
                         ${e.toPos.x} ${e.toPos.y - 28}`}
                  stroke="#D8D4CE" strokeWidth="1" fill="none" />
          )
        ))}
        {layout.nodes.map(n => {
          const color = ecoColor(n.ecosystem_code);
          const band = BAND_TONE[n.rss_band] || null;
          return (
            <g key={n.id}
               data-testid={`graph-node-${n.id}`}
               transform={`translate(${n.x - 90}, ${n.y - 28})`}
               onClick={() => onNodeClick(n.id)}
               style={{ cursor: 'pointer' }}>
              <rect width="180" height="56" rx="10" ry="10"
                    fill="#FFFFFF"
                    stroke={band ? band.ring : color} strokeWidth={band ? "2" : "1"} />
              <circle cx="14" cy="14" r="5" fill={color} />
              <text x="26" y="18" fontSize="11" fill="#2C2A29"
                    style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {(n.name || '—').slice(0, 22)}
              </text>
              <text x="26" y="34" fontSize="9" fill="#8A8681"
                    style={{ fontFamily: 'Work Sans, sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                {(n.perception_state || '').replace(/_/g, ' ').toLowerCase()}
              </text>
              <text x="26" y="48" fontSize="9" fill="#5C5955"
                    style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {n.child_count > 0 ? `${n.child_count} referrals` : 'leaf'}
                {band && ` · ${band.label} RSS`}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
